/** When true, skip the "offline mode" check so callers (e.g. Sync flow) can get a token to sync while still marked offline. */
export async function getAccessToken(token?: string, expiry?: number, forceForSync?: boolean) {
    // CRITICAL: Check if offline mode is active - if so, don't make API calls (unless forceForSync = true for sync flow)
    if (!forceForSync) {
        try {
            const storeModule = await import("../store/store");
            const store = storeModule.default;
            const isOfflineStarted = store.getState()?.appState?.isOfflineStarted || false;
            if (isOfflineStarted) {
                console.warn('getAccessToken: Offline mode is active - skipping API call');
                return null;
            }
        } catch (e) {
            // Store might not be available, continue
        }
    }

    // CRITICAL: Check network status - if offline, don't make API calls
    if (!navigator.onLine) {
        console.warn('getAccessToken: Network is offline - skipping API call');
        return null;
    }

    const currentTime = new Date().getTime() / 1000; // Current time in seconds
    // ENV_STAMP identifies which Power Automate flow minted the cached token.
    // Must match between reads and writes — change to "dev" when switching to the dev flow URL.
    const ENV_STAMP = "prod"; // Keep in sync with the active flowUrl below
    // If token and expiry are provided, store and return them
    if (token && expiry) {
        if (expiry > currentTime) {
            return { token: token, expiry: expiry };
        }
    }
    const storedToken = JSON.parse(localStorage.getItem("access_token") || "null");
    // Return cached token only if it is still valid AND was issued by the same environment's flow.
    // Tokens from a different env (e.g. stale dev token) would cause 401s on the prod Dynamics org.
    if (storedToken && storedToken.expires_at > currentTime && storedToken.env === ENV_STAMP) {
        return { token: storedToken.token, expiry: storedToken.expires_at };
    } else {
        // Clear stale, expired, or wrong-env token
        localStorage.removeItem("access_token");
    }
    // const flowUrl = "https://86c49df27027e13c808b32506fa981.d1.environment.api.powerplatform.com:443/powerautomate/automations/direct/cu/11/workflows/182bf483e5b84314aba7baf2612543fc/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=oE7DjCwm3S0c-8PZlVFIYGCIL61LybVqodXyqq9U0HY"; // Dev Power Automate flow URL
    const flowUrl = "https://defaultaa44c5c154484e7488d917838a6f9d.5a.environment.api.powerplatform.com:443/powerautomate/automations/direct/workflows/7b6770178a5b424baddc640faef80342/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=nuoXJf61QZZVrfx9lRTTHUVmNjBDN1TNdGIQhU8j6No"; // Prod Power Automate flow URL


    try {
        const response = await fetch(flowUrl, {
            method: "POST", // Ensure this matches the method expected by your flow
            headers: {
                "Content-Type": "application/json", // Required by the flow
            },
            body: JSON.stringify({}) // Send an empty JSON object if required
        });
        if (!response.ok) {
            throw new Error(`Failed to fetch token from Power Automate: ${response.statusText}`);
        }
        const data = await response.json(); // Assuming the flow returns a JSON object with the token and expiry
        const expiresAt = currentTime + data.expires_in;
        // Store the token, expiration, and env stamp in localStorage
        localStorage.setItem(
            "access_token",
            JSON.stringify({
                token: data.access_token,
                expires_at: expiresAt,
                env: ENV_STAMP  // Stamp the env so stale cross-env tokens are auto-rejected
            })
        );
        return { token: data.access_token, expiry: expiresAt };
    } catch (error) {
        console.error("Error fetching token from Power Automate:", error);
        return null;
    }
} 